import type {
	DeliveryWindow,
	SharingType,
	Location,
	TaskNote,
	UIData,
	datetime,
	TimeWindow,
	WayPoint,
	TaskStatus,
	SharedLocationDisplayType
} from '@bringg/types';

export type OldSharedLocationConfig = UIData & {
	uuid: string;
	expired: boolean;
	arrived: boolean;
	embedded: boolean;
	done: boolean;
	rate_expired: boolean;
	message: string;
	shared_type: SharingType;
	order_external_id: string;
	customer_name: string;
	allow_rating: boolean;
	allow_cx_customer_cancellation: boolean;
	task_started: datetime;
	cx_cancel_event_type: number;
	cancel_token: string;
	rating_token: string;
	rating_reason: RatingReason;
	driver_uuid: string;
	driver_id: string;
	driver_assigned: number;
	order_uuid: string;
	way_point_id: number;
	privateMode: boolean;
	task_id: number;
	forwarded: boolean;
	forwarded_origin_customer_name: string;
	destination: string;
	eta: string;
	action_tokens: ActionTokens;
	team_name: string;
	customer_pickup?: CustomerPickup;
	max_zoom: number;
	rate_us_title_string: string;
	enable_vehicle_details_flow: boolean;
	is_curbside_flow: boolean;
	curbside_flow_type: CurbsideFlowType;
	customer_flow_type: CustomerFlowType;
	redirectDeliveryConfiguration: RedirectDeliveryConfiguration;
	opt_in_out_data: OptInOutData;
	delivery_window: DeliveryWindow;
	isUserAuthenticated: boolean;
	waypoint: TimeWindow;
	is_green_delivery: boolean;
	display_type: SharedLocationDisplayType;
	no_earlier_than: datetime;
	set_min_eta_by_time_window: boolean;
	disable_cx_color_manipulation: boolean;
	calendar_view_for_delivery_options: boolean;
	order_status: TaskStatus;
	cx_scheduling_customer_message_title?: string;
	cx_scheduling_free_comment_input_label?: string;
	hide_powered_by_logo: boolean;
	enforce_end_user_timezone: boolean;
};

export type PartialOldSharedLocationConfig = Omit<
	OldSharedLocationConfig,
	| 'customer_name'
	| 'order_external_id'
	| 'delivery_window'
	| 'languageCode'
	| 'header_color'
	| 'merchant_logo'
	| 'bringg_logo'
	| 'eta_title_color'
	| 'vehicle_details_form_configuration'
	| 'timezone'
	| 'waypoint'
>;

export type BaseSharedLocationConfig = PartialOldSharedLocationConfig & {
	shared_location_uuid: string;
	customer: {
		name: string;
	};
	task: { external_id: string };
	ui_data: Partial<UIData>;
	waypoint: Partial<WayPoint>;
	employee_name?: string;
	job_description?: string;
	employee_image?: string;
	cancel_order_message?: string;
};

export type OptInOutData = ContactNotificationsOptions & {
	terms_and_conditions_text?: string;
};

export type RedirectDeliveryConfiguration = {
	enabled?: boolean;
	teams?: (Partial<Bringg.Team> & { distance: number })[];
};

export enum CancelEventType {
	DRIVER_ASSIGNED = 0,
	TASK_STARTED
}

export type CustomerPickup = {
	number: number;
	color: string;
	vehicle: CarArrivedData;
};

export type ActionTokens = {
	checkin: string;
	checkout: string;
	update_eta: string;
};

export type RatingReason = {
	use: boolean;
	rating: number;
	rating_reason_url: string;
	items: RatingReasonItem[];
	title: string;
	positive_title: string;
};

export type RatingReasonItem = {
	id: number;
	text: string;
};

export type ScheduleConfig = OldSharedLocationConfig & {
	reschedule: Reschedule;
};

export type TrackingConfig = BaseSharedLocationConfig & {
	employee_image: string;
	employee_name: string;
	employee_phone: string;
	note_token: string;
	current_lat: number;
	current_lng: number;
	destination_lat: number;
	destination_lng: number;
	destination: string;
	eta: string;
	driver_activity: DriverActivity;
	driver_arrived_caption_string: string;
	driver_arrived_string: string;
	find_me_token: string;
	job_description: string;
	support_find_me: boolean;
	task_forwarding_enabled: boolean;
	hide_call_to_driver: boolean;
	hide_note_to_driver: boolean;
	deliveryPin: string;
	destinationPin: string;
	show_delivery_status: boolean;
	hide_footer: boolean;
	linked_task_status: TaskStatus;
	allow_street_view: boolean;
	route: boolean;
	numberOfTasksLeftInRun: number;
	mask_phone_numbers: boolean;
	merchant_name: string;
	customer_app_labels: string[];
	share_to_customer_notes: TaskNote[];
};

export type Reschedule = {
	delivery_windows: DeliveryWindow[];
	update_reschedule_options_url?: string;
	update_reschedule_url?: string;
	update_reschedule_configuration?: RescheduleConfiguration;
	update_reschedule_count?: number;
};

export type RescheduleConfiguration = {
	enabled: boolean;
	cutoff_hours: number;
	max_reschedules_per_order: number;
	unlimited_reschedules: boolean;
	days_before: number;
	days_after: number;
	max_displayed_results: number;
};

export type RescheduleOption = DeliveryWindow & {
	id: number | string;
};

export type RescheduleOptionsResponse = {
	success: boolean;
	reschedule_options: RescheduleOption[];
};

export type ContactNotificationsOptions = Partial<{
	allow_send_email: boolean;
	allow_send_sms: boolean;
}>;

export type ScheduleUpdateParams = ContactNotificationsOptions & {
	delivery_window_id: number | string;
	start_time: string;
	end_time: string;
};

export type UpdateWayPointLocation = {
	taskId: number;
	sharedLocationUuid: string;
	position: GeolocationPosition;
	findMeToken: string;
};

export type RateParams = {
	rating: number;
	reason_ids: number[];
	comment: string;
	sharedLocationUuid: string;
	token: string;
};

export type SendNoteParams = {
	note: string;
	token: string;
	isUserAuthenticated: boolean;
};

export type ForwardSharedLocationContact = Partial<{
	name: string;
	phone: string;
	email: string;
}>;

export type ForwardSharedLocationParams = ForwardSharedLocationContact & {
	uuid: string;
};

export type LocationUpdate = Location & {
	uuid: string;
};

export type WayPointLocationUpdate = {
	lat: string;
	lng: string;
};

export type WayPointEtaUpdate = {
	eta: string;
};

export type DriverActivityUpdate = {
	activity: DriverActivity;
};

export enum RealTimeEvents {
	WatchWayPoint = 'watch way point',
	WatchDriver = 'watch driver',
	WatchOrder = 'watch order',
	WayPointEtaUpdate = 'way point eta updated',
	WayPointLocationUpdate = 'way point location updated',
	WayPointArrived = 'way point arrived',
	WayPointDone = 'way point done',
	OrderDone = 'order done',
	OrderCancelled = 'order cancelled',
	OrderUpdated = 'order update',
	LinkedOrderUpdated = 'linked order update',
	DriverLocationUpdate = 'location update',
	DriverActivityUpdate = 'activity change'
}

export enum DriverActivity {
	Unknown,
	NotMoving,
	Walking,
	Running,
	Bicycle,
	Driving,
	PublicTransport
}

export enum CurbsideFlowType {
	ClickAndCollect = 1,
	Curbside,
	Dynamic
}

export enum CustomerFlowType {
	ClickAndCollect,
	Curbside
}

export enum GoogleTravelMode {
	BICYCLING = 'BICYCLING',
	DRIVING = 'DRIVING',
	TRANSIT = 'TRANSIT',
	TWO_WHEELER = 'TWO_WHEELER',
	WALKING = 'WALKING'
}

export type CarArrivedData = {
	id?: number;
	license_plate?: string;
	model?: string;
	color?: string;
	parking_spot?: string;
	save_vehicle?: boolean;
};

export type MaskedPhoneResponse = {
	phone_number: string;
	status: string;
};
